// VendorPreviewModal — the one thing missing from the PO import review screen:
// a way to actually SEE the vendor detail that flows through it. The data was
// always there (matched_vendor_id, create_proposal, enrichment), it just never
// rendered anywhere richer than a name. This is a shared, read-only modal
// opened from a vendor name/chip in PoImportDocumentCard, PoImportVendorTriage
// and (existing-vendor mode only) PurchaseOrderDetail.
//
// Two data modes:
//   'existing' — fetches the gated GET /procurement/vendors/:id and renders
//                the full row (same shape VendorsAdmin edits). An optional
//                `enrichment` overlay marks which of THOSE fields this PO will
//                fill in — shown only where the real row is still blank,
//                mirroring vendors-service.js: enrichment only ever writes an
//                empty column, never overwrites one.
//   'proposal' — nothing has been created yet. Renders straight off the
//                CURRENT create decision (poImportDraft.ts vendorFieldsForGroup
//                / vendorCreateFromProposal), badged as a proposal so it can
//                never be mistaken for something already on file.
//
// Read-only, both modes: this modal never edits a vendor itself. The one
// exception is `onEdit`, an optional escape hatch VendorsAdmin wires up so a
// row-click preview can go straight to editing without a second fetch (the
// preview already holds the exact VendorFullRow the edit form needs). Callers
// that don't pass it (the PO import review screen, PurchaseOrderDetail) stay
// pure read-only.

import React, { useEffect, useState } from 'react';
import { Alert } from '../components/ui/Alert';
import FadeIn from '../components/ui/FadeIn';
import { buttonClass } from '../components/ui/Button';
import { Skeleton, SkeletonGroup } from '../components/ui/Skeleton';
import { useApi } from '../apiHelpers';
import DrawerShell from '../Receiving/DrawerShell';
import { formatType } from './vendorTypeLabels';
// Type-only: erased at compile time, so this does NOT create a runtime edge
// back to VendorsAdmin.tsx (which imports this component for its row-click
// preview) — see vendorTypeLabels.ts for why formatType itself had to move.
import type { VendorFullRow } from '../Admin/VendorsAdmin';
import type { ConfirmVendorCreate } from './poImportApi';

export type VendorPreviewSubject =
  | { mode: 'existing'; vendorId: string; vendorName?: string; enrichment?: Record<string, string> | null }
  | { mode: 'proposal'; proposal: ConfirmVendorCreate };

interface Props {
  subject: VendorPreviewSubject;
  onClose: () => void;
  /** Existing-mode only. When provided, an "Edit" button hands the already-
   * fetched full row to the caller and closes the preview in one step — "one
   * hop from looking to fixing" rather than a second GET /vendors/:id. */
  onEdit?: (row: VendorFullRow) => void;
}

const labelClass = 'block text-[11px] uppercase tracking-wider text-on-surface-variant mb-0.5';

interface DisplayField {
  value: string;
  /** True when this value is not on the real row yet — it's what an
   * enrichment proposal from THIS purchase order would fill in. */
  harvested: boolean;
}

/** A blank vendor column, backed by an enrichment proposal, still reads as
 * "harvested" rather than "already there" — that distinction is the entire
 * point of this modal, so it is never allowed to collapse into a plain value. */
function harvestField(current: string | null, key: string, enrichment: Record<string, string> | null | undefined): DisplayField {
  if (current) return { value: current, harvested: false };
  const proposed = enrichment?.[key];
  return proposed ? { value: proposed, harvested: true } : { value: '—', harvested: false };
}

const Field: React.FC<{ label: string; value: React.ReactNode; harvested?: boolean }> = ({ label, value, harvested }) => (
  <div>
    <span className={labelClass}>{label}</span>
    <div className={`text-sm ${harvested ? 'text-indigo-300' : 'text-on-surface'}`}>
      {value}
      {harvested && (
        <span className="ml-1.5 inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-500/10 text-indigo-300 ring-1 ring-inset ring-indigo-500/25 align-middle">
          from this PO
        </span>
      )}
    </div>
  </div>
);

const ExistingVendorView: React.FC<{ row: VendorFullRow; enrichment: Record<string, string> | null | undefined }> = ({
  row,
  enrichment,
}) => (
  <div className="space-y-4">
    <div className="flex items-center gap-2 flex-wrap">
      <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-700/40 text-on-surface-variant ring-1 ring-inset ring-[rgb(70_70_70/0.40)]">
        {formatType(row.type)}
      </span>
      {row.code && <span className="font-mono text-xs text-primary">{row.code}</span>}
      {row.is_disabled && (
        <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-700/50 text-on-surface-variant ring-1 ring-inset ring-[rgb(70_70_70/0.40)]">
          Disabled
        </span>
      )}
    </div>

    {row.on_hold && (
      <Alert tone="warning" variant="note" live={false} className="!flex">
        On hold{row.hold_reason ? `: ${row.hold_reason}` : ''}
        {row.release_date ? ` · lifts ${row.release_date}` : ''}
      </Alert>
    )}

    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
      <Field label="Contact name" {...harvestField(row.contact_name, 'contact_name', enrichment)} />
      <Field label="Contact email" {...harvestField(row.contact_email, 'contact_email', enrichment)} />
      <Field label="Contact phone" {...harvestField(row.contact_phone, 'contact_phone', enrichment)} />
    </div>

    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
      <Field label="Address line 1" {...harvestField(row.address_line1, 'address_line1', enrichment)} />
      <Field label="Address line 2" {...harvestField(row.address_line2, 'address_line2', enrichment)} />
      <Field label="City" {...harvestField(row.city, 'city', enrichment)} />
      <Field label="Region" {...harvestField(row.region, 'region', enrichment)} />
      <Field label="Postal code" {...harvestField(row.postal_code, 'postal_code', enrichment)} />
      <Field label="Country" {...harvestField(row.country, 'country', enrichment)} />
    </div>

    <div className="grid grid-cols-2 gap-4">
      <Field label="Payment terms" {...harvestField(row.payment_terms, 'payment_terms', enrichment)} />
      <Field label="QBO vendor ref" value={row.qbo_vendor_ref || '—'} />
    </div>

    {row.notes && (
      <div>
        <span className={labelClass}>Notes</span>
        <p className="text-sm text-on-surface whitespace-pre-wrap mt-0.5">{row.notes}</p>
      </div>
    )}
  </div>
);

const ProposalVendorView: React.FC<{ proposal: ConfirmVendorCreate }> = ({ proposal }) => (
  <div className="space-y-4">
    <div className="flex items-center gap-2 flex-wrap">
      <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-primary/10 text-primary-ink ring-1 ring-inset ring-primary/30">
        New vendor — will be created on confirm
      </span>
      {proposal.is_disabled && (
        <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-700/50 text-on-surface-variant ring-1 ring-inset ring-[rgb(70_70_70/0.40)]">
          Starts disabled
        </span>
      )}
    </div>

    <Field
      label="Type"
      value={proposal.type ? formatType(proposal.type) : <span className="text-amber-400">Not chosen yet</span>}
    />

    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
      <Field label="Contact name" value={proposal.contact_name || '—'} />
      <Field label="Contact email" value={proposal.contact_email || '—'} />
      <Field label="Contact phone" value={proposal.contact_phone || '—'} />
    </div>

    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
      <Field label="Address line 1" value={proposal.address_line1 || '—'} />
      <Field label="Address line 2" value={proposal.address_line2 || '—'} />
      <Field label="City" value={proposal.city || '—'} />
      <Field label="Region" value={proposal.region || '—'} />
      <Field label="Postal code" value={proposal.postal_code || '—'} />
      <Field label="Country" value={proposal.country || '—'} />
    </div>

    <Field label="Payment terms" value={proposal.payment_terms || '—'} />

    <p className="text-xs text-on-surface-variant">
      Harvested from this purchase-order batch. Nothing is created until the batch is confirmed.
    </p>
  </div>
);

const VendorPreviewModal: React.FC<Props> = ({ subject, onClose, onEdit }) => {
  const api = useApi();
  const isExisting = subject.mode === 'existing';

  const [row, setRow] = useState<VendorFullRow | null>(null);
  const [loading, setLoading] = useState(isExisting);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (subject.mode !== 'existing') return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setRow(null);
    (async () => {
      try {
        const data = (await api.get(`/procurement/vendors/${subject.vendorId}`)) as VendorFullRow;
        if (!cancelled) setRow(data);
      } catch (err: any) {
        if (!cancelled) setLoadError(err?.data?.error?.message || err?.message || 'Failed to load this vendor.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Deliberately narrowed to the vendor id, not the whole `subject` object —
    // a proposal-mode subject has no id to key on, and re-running this for an
    // unrelated `enrichment`/`vendorName` change on the same vendor would just
    // re-fetch an identical row.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subject.mode === 'existing' ? subject.vendorId : null]);

  const title = isExisting ? row?.name || subject.vendorName || 'Vendor' : subject.proposal.name || 'New vendor';

  return (
    <DrawerShell title={title} onClose={onClose} size="lg">
      {isExisting ? (
        loading ? (
          <SkeletonGroup label="Loading this vendor" className="space-y-3 py-4">
            <Skeleton className="h-5 w-1/3" />
            <Skeleton className="h-16 w-full" />
          </SkeletonGroup>
        ) : loadError ? (
          <FadeIn key="error"><Alert tone="error" variant="text" className="py-4">{loadError}</Alert></FadeIn>
        ) : row ? (
          <FadeIn key="loaded" className="space-y-4">
            <ExistingVendorView row={row} enrichment={subject.enrichment} />
            {onEdit && (
              <div className="flex justify-end pt-2 border-t border-gray-700/50">
                <button
                  type="button"
                  onClick={() => {
                    onEdit(row);
                    onClose();
                  }}
                  className={buttonClass('filled')}
                >
                  Edit
                </button>
              </div>
            )}
          </FadeIn>
        ) : null
      ) : (
        <ProposalVendorView proposal={subject.proposal} />
      )}
    </DrawerShell>
  );
};

export default VendorPreviewModal;
